import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { forEachDiagnostic } from "@codemirror/lint";
import {
  ChevronDown,
  CircleX,
  Cloud,
  Info,
  Laptop,
  LifeBuoy,
  Play,
  Plus,
  RotateCcw,
  Square,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { can, type FsSyncState } from "@ide/shared";
import { Button } from "@/components/ui/button";
import { Menu, MenuContent, MenuItem, MenuTrigger, Tip } from "@/components/ui/overlay";
import { StatusDot } from "@/components/ui/primitives";
import { del, errorMessage, post } from "@/lib/api";
import type { SandboxTicket } from "@/lib/types";
import { useTextSize } from "@/lib/text-size";
import { useTheme } from "@/lib/theme";
import { cn, modKey } from "@/lib/utils";
import { useUiThemeClasses } from "@/plugins/use-plugins";
import {
  LocalTerminalConnection,
  TerminalConnection,
  terminalTheme,
  type AnyTerminal,
  type TerminalStatus,
} from "@/terminal/terminal-client";
import { desktop, isMac } from "@/desktop/bridge";
import { readPlanFiles, runsAvailable, startLive } from "@/runs/local-runs";
import { isStaticSite } from "@/runs/plan";
import { useOnboarding } from "@/lib/home";
import { hasCloud, preferredTerminal } from "./environment";
import { detectRunCommand } from "@/runs/run-command";
import { activeWorkspace } from "./file-ops";
import { enableLocalCopy, flushLocalCopy, localCopyAvailable, useLocalCopy } from "./local-copy";
import { activeEditor } from "./active-editor";
import { logSessionEvent } from "./activity";
import { CommentsList, useCommentCount } from "./comments";
import { useRole, useTree, useWs } from "./context";
import { logOutput, useOutput } from "./output";
import { OutputList } from "./output-panel";
import { useWorkspace, type BottomTab } from "./store";

/** Terminais por sandbox (o agente do sandbox recusa além disso: MAX_TERMINALS). */
export const MAX_TERMINALS = 4;

export type TerminalKind = "local" | "sandbox";

export interface TerminalInfo {
  id: string;
  /** local: shell da máquina da pessoa (app desktop); sandbox: shell na nuvem. */
  kind: TerminalKind;
  /** Número mostrado na aba (1, 2, 3…), não reaproveitado na sessão. */
  n: number;
  shell: string | null;
  status: TerminalStatus;
  fs: { state: FsSyncState; pending: number } | null;
  /** Saída chegando agora. */
  busy: boolean;
  connection: AnyTerminal | null;
}

interface TerminalStore {
  terminals: TerminalInfo[];
  activeId: string | null;
  /** Do terminal ativo (barra de status, botão Executar, comandos do menu). */
  connection: AnyTerminal | null;
  status: TerminalStatus;
  fs: { state: FsSyncState; pending: number } | null;
  /** Algum terminal já foi aberto nesta sessão do projeto (o sandbox foi pedido). */
  started: boolean;
  add(kind?: TerminalKind): string | null;
  remove(id: string): void;
  select(id: string): void;
  update(id: string, patch: Partial<TerminalInfo>): void;
  reset(): void;
}

let counter = 0;

function mirror(terminals: TerminalInfo[], activeId: string | null) {
  const t = terminals.find((x) => x.id === activeId) ?? null;
  return {
    terminals,
    activeId: t?.id ?? null,
    connection: t?.connection ?? null,
    status: t?.status ?? ("idle" as TerminalStatus),
    fs: t?.fs ?? null,
    started: terminals.length > 0,
  };
}

/** Terminais da pessoa (cada um é um shell próprio no sandbox individual). */
export const useTerminalStore = create<TerminalStore>((set, get) => ({
  ...mirror([], null),
  add: (kind = defaultKind()) => {
    const { terminals } = get();
    if (kind === "sandbox" && terminals.filter((t) => t.kind === "sandbox").length >= MAX_TERMINALS) {
      toast(`Até ${MAX_TERMINALS} terminais na nuvem. Feche um para abrir outro.`);
      return null;
    }
    if (terminals.length >= MAX_TERMINALS * 2) {
      toast("Muitos terminais abertos. Feche um para abrir outro.");
      return null;
    }
    const t: TerminalInfo = {
      id: `t${++counter}`,
      kind,
      n: Math.max(0, ...terminals.map((x) => x.n)) + 1,
      shell: null,
      status: "idle",
      fs: null,
      busy: false,
      connection: null,
    };
    set(mirror([...terminals, t], t.id));
    return t.id;
  },
  remove: (id) => {
    const { terminals, activeId } = get();
    const idx = terminals.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const rest = terminals.filter((t) => t.id !== id);
    const next = activeId === id ? (rest[Math.max(0, idx - 1)]?.id ?? null) : activeId;
    set(mirror(rest, next));
  },
  select: (id) => set((s) => mirror(s.terminals, id)),
  update: (id, patch) =>
    set((s) =>
      mirror(
        s.terminals.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        s.activeId,
      ),
    ),
  reset: () => set(mirror([], null)),
}));

// Primeiros passos (tela inicial): o primeiro terminal pronto conta, inclusive o da máquina.
useTerminalStore.subscribe((s) => {
  if (s.status === "ready" && !useOnboarding.getState().terminal) useOnboarding.getState().ranTerminal();
});

const STATUS_LABEL: Record<TerminalStatus, string> = {
  idle: "parado",
  starting: "iniciando…",
  connecting: "conectando…",
  ready: "pronto",
  reconnecting: "reconectando…",
  exited: "encerrado",
  error: "erro",
};

/** No app, o terminal padrão é o da máquina (com a cópia local ligada); na web, o do sandbox. */
function defaultKind(): TerminalKind {
  // O ambiente escolhido na barra do projeto (Nuvem ou Local) decide; Local precisa da cópia local.
  return preferredTerminal();
}

/**
 * Abre um terminal novo (e o painel). Terminal local sem a cópia local ligada: liga primeiro
 * (o shell roda na pasta do projeto na máquina).
 */
/** Visualizadores não têm terminal (nem o da máquina) e não rodam comandos. */
function terminalBlocked(): boolean {
  const role = activeWorkspace.current?.session.role ?? activeWorkspace.current?.detail.role;
  if (can(role, "terminal")) return false;
  toast("Visualizadores não usam o terminal", {
    id: "viewer-terminal",
    description: "Peça ao dono um papel de editor para rodar comandos no projeto.",
  });
  return true;
}

export function newTerminal(kind?: TerminalKind) {
  useWorkspace.getState().setBottomTab("terminal");
  if (terminalBlocked()) return null;
  let wanted = kind ?? defaultKind();
  // A nuvem é do plano Business (de cada pessoa): sem ele, o terminal é o da máquina.
  if (wanted === "sandbox" && !hasCloud()) {
    toast("A nuvem é do plano Business", {
      id: "cloud-business",
      description: "Contêiner próprio com todas as linguagens. Sem o plano, o terminal roda na sua máquina.",
    });
    if (!localCopyAvailable) return null;
    wanted = "local";
  }
  if (wanted === "local" && !useLocalCopy.getState().info?.enabled) {
    const ws = activeWorkspace.current;
    if (!ws) return null;
    void enableLocalCopy(ws.session, { id: ws.detail.project.id, name: ws.detail.project.name }, false).then(
      (ok) => ok && useTerminalStore.getState().add("local"),
    );
    return null;
  }
  return useTerminalStore.getState().add(wanted);
}

/** Encerra o terminal (o shell dele no sandbox também é encerrado). */
export function killTerminal(id = useTerminalStore.getState().activeId) {
  if (id) useTerminalStore.getState().remove(id);
}

/** "Executar" a partir de qualquer lugar: abre o terminal, sobe o sandbox se preciso, salva e roda. */
export function runProject(session: { setPresence: (p: { activity: "running" | "editing" }) => void }) {
  return runInTerminal(session);
}

/** Comando pronto, ou montado conforme o terminal (máquina da pessoa ou sandbox). */
export type RunCommand = string | ((target: TerminalKind) => string | null);

/**
 * Roda um comando no terminal ativo DA PESSOA (o sandbox é individual): abre o painel, espera o
 * sandbox, sincroniza o disco com o código compartilhado e digita o comando. Sem comando, usa o
 * "Executar" do projeto; site estático (HTML, CSS e JS de página) no app vai para o Live Server.
 * `dedicated`: abre um terminal novo só para essa execução (como o "Run in Dedicated Terminal").
 */
export async function runInTerminal(
  session: { setPresence: (p: { activity: "running" | "editing" }) => void },
  command?: RunCommand,
  options: { dedicated?: boolean; label?: string } = {},
) {
  useWorkspace.getState().setBottomTab("terminal");
  if (terminalBlocked()) return;
  const store = useTerminalStore.getState();
  if (options.dedicated && store.terminals.length) {
    if (!newTerminal()) return;
  } else if (!store.terminals.length) store.add();
  else if (!store.connection) store.select(store.terminals[store.terminals.length - 1]!.id);
  for (let i = 0; i < 100 && useTerminalStore.getState().status !== "ready"; i++) {
    await new Promise((r) => setTimeout(r, 200));
  }
  const { connection: conn, terminals, activeId } = useTerminalStore.getState();
  if (!conn || conn.status !== "ready") {
    toast.error("O terminal ainda não está pronto.");
    logOutput("Executar", "O terminal não ficou pronto a tempo.", "error");
    return;
  }
  let cmd = typeof command === "function" ? command(conn.kind) : command;
  if (typeof command === "function" && !cmd) {
    toast("Não há como rodar este arquivo aqui.");
    return;
  }
  if (!cmd) {
    const detected = await detectRun(conn.kind);
    if (detected === STATIC_SITE) {
      // HTML, CSS e JS de página: abre no navegador pelo Live Server (recarrega a cada alteração).
      const ws = activeWorkspace.current;
      if (ws && runsAvailable) {
        logOutput("Executar", "Site estático: aberto em Pré-visualizar (servidor local).");
        void startLive({ id: ws.detail.project.id, name: ws.detail.project.name });
        return;
      }
      cmd = null;
    } else cmd = detected ?? conn.runCommand ?? null;
  }
  if (!cmd) {
    toast("Não achei como rodar este projeto", {
      description: "Defina o comando em Executar ▸ Comando (ex.: npm start, python main.py).",
    });
    return;
  }
  const n = terminals.find((t) => t.id === activeId)?.n;
  session.setPresence({ activity: "running" });
  const synced = await conn.run(cmd);
  logOutput("Executar", `${options.label ? `${options.label}: ` : ""}${cmd} — terminal ${n ?? ""}`.trim());
  logSessionEvent({ kind: "run", who: "Você", text: `rodou ${cmd} (terminal ${n ?? 1})` });
  if (!synced && conn.kind === "sandbox") {
    toast("Rodando, mas a última edição pode não ter chegado ao terminal.");
    logOutput("Executar", "A última edição não chegou ao terminal antes de rodar.", "warn");
  }
}

/** Resposta de {@link detectRun} para site estático (vai para o Live Server no app). */
const STATIC_SITE = "\u0000static";

/** Comando do Executar: o do projeto, ou o lido dos arquivos (máquina local ou sandbox). */
async function detectRun(kind: TerminalKind): Promise<string | null> {
  const ws = activeWorkspace.current;
  if (!ws) return null;
  if (ws.detail.project.runCommand) return ws.detail.project.runCommand;
  const files = await readPlanFiles(ws.session);
  if (kind === "local" && isStaticSite(files)) return STATIC_SITE;
  return detectRunCommand(files, kind === "local" ? "local" : "sandbox", desktop?.platform ?? "linux");
}

/** Copiar/colar no terminal: área de transferência do sistema (app) ou do navegador. */
export function copyText(text: string) {
  if (desktop?.clipboard) desktop.clipboard.write(text);
  else void navigator.clipboard?.writeText(text).catch(() => {});
}

export async function readClipboard(): Promise<string> {
  if (desktop?.clipboard) return desktop.clipboard.read();
  try {
    return (await navigator.clipboard?.readText()) ?? "";
  } catch {
    toast("Permita o acesso à área de transferência para colar com o botão direito (ou use Ctrl+V).");
    return "";
  }
}

/** Pedido de ajuda ao time (presença), comum a todos os terminais. */
const useHelp = create<{ needsHelp: boolean }>(() => ({ needsHelp: false }));

/** Um terminal (xterm + conexão). Fica montado enquanto existe: trocar de aba não derruba o shell. */
function TerminalInstance({ info, visible }: { info: TerminalInfo; visible: boolean }) {
  const { session, detail } = useWs();
  const dark = useTheme((s) => s.theme === "dark");
  const uiTheme = useUiThemeClasses();
  const host = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const { id, n } = info;

  useEffect(() => {
    if (!host.current) return;
    const update = useTerminalStore.getState().update;
    const term = new Terminal({
      cursorBlink: true,
      // Fallbacks com símbolos (✔ ✖ ℹ dos relatórios de teste) antes da fonte de emoji do sistema.
      fontFamily:
        '"JetBrains Mono Variable", "Cascadia Mono", "DejaVu Sans Mono", "Segoe UI Symbol", "Noto Sans Symbols 2", ui-monospace, monospace',
      fontSize: useTextSize.getState().terminalFont,
      lineHeight: 1.25,
      theme: terminalTheme(useTheme.getState().theme === "dark"),
      allowProposedApi: false,
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(host.current);
    termRef.current = term;
    fitRef.current = fit;
    requestAnimationFrame(() => {
      try {
        fit.fit();
      } catch {
        // contêiner ainda sem tamanho
      }
    });

    const stuck = () => useWorkspace.getState().stuckDetection;
    const conn: AnyTerminal =
      info.kind === "local" && desktop?.terminal
        ? new LocalTerminalConnection(
            term,
            desktop.terminal,
            detail.project.id,
            stuck,
            () => detectRun("local"),
            flushLocalCopy,
          )
        : new TerminalConnection(
            term,
            () => post<SandboxTicket>(`/projects/${detail.project.id}/sandbox`),
            stuck,
            () => detectRun("sandbox"),
          );

    // Como no VS Code: selecionar copia; botão direito cola (texto copiado na IDE ou fora dela).
    const selection = term.onSelectionChange(() => {
      const text = term.getSelection();
      if (text) copyText(text);
    });
    const onContext = (e: MouseEvent) => {
      e.preventDefault();
      void readClipboard().then((text) => text && term.paste(text));
    };
    host.current.addEventListener("contextmenu", onContext);
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== "keydown") return true;
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const key = e.key.toLowerCase();
      // Ctrl+C com seleção copia (sem seleção, segue como interromper); Ctrl+Shift+C também.
      if (mod && key === "c" && (e.shiftKey || (!isMac && term.hasSelection()))) {
        copyText(term.getSelection());
        term.clearSelection();
        e.preventDefault();
        return false;
      }
      if (mod && key === "v") {
        e.preventDefault();
        void readClipboard().then((text) => text && term.paste(text));
        return false;
      }
      return true;
    });
    const hostEl = host.current;
    let lastStatus: TerminalStatus = "idle";
    let fsError = false;
    const offs = [
      conn.on("status", (s) => {
        update(id, { status: s });
        if (s === "ready") session.setPresence({ activity: "editing" });
        if (s !== lastStatus && s !== "connecting" && s !== "idle") {
          logOutput(
            "Terminal",
            `Terminal ${n}: ${STATUS_LABEL[s]}`,
            s === "error" ? "error" : s === "reconnecting" || s === "exited" ? "warn" : "info",
          );
        }
        lastStatus = s;
      }),
      conn.on("fs", (f) => {
        update(id, { fs: f });
        if (f.state === "error" && !fsError) {
          fsError = true;
          logOutput("Sincronização", "As últimas edições não chegaram ao terminal na nuvem.", "error");
        } else if (f.state === "synced" && fsError) {
          fsError = false;
          logOutput("Sincronização", "Terminal em dia de novo.");
        }
      }),
      conn.on("shell", (shell) => update(id, { shell })),
      conn.on("activity", (busy) => update(id, { busy })),
      conn.on("error", (m) => {
        toast.error(m);
        logOutput("Terminal", `Terminal ${n}: ${m}`, "error");
      }),
      conn.on("stuck", (stuck) => {
        if (!stuck) return;
        session.setPresence({ needsHelp: true, activity: "stuck" });
        useHelp.setState({ needsHelp: true });
        toast("Parece que você está travado", {
          description: "Avisamos o time. Só o aviso aparece — nada do seu terminal é compartilhado.",
          action: {
            label: "Desfazer",
            onClick: () => {
              session.setPresence({ needsHelp: false, activity: "editing" });
              useHelp.setState({ needsHelp: false });
              conn.clearStuck();
            },
          },
        });
      }),
    ];
    update(id, { connection: conn });
    void conn.connect();

    const ro = new ResizeObserver(() => {
      try {
        fit.fit();
      } catch {
        // ignorado durante animações de layout
      }
    });
    ro.observe(host.current);
    return () => {
      ro.disconnect();
      selection.dispose();
      hostEl.removeEventListener("contextmenu", onContext);
      offs.forEach((o) => o());
      conn.dispose();
      term.dispose();
      termRef.current = null;
    };
  }, [id, n, info.kind, detail.project.id, session]);

  // Letra do terminal escolhida pela pessoa: aplica em todos os terminais abertos e reajusta o tamanho.
  const terminalFont = useTextSize((s) => s.terminalFont);
  useEffect(() => {
    const term = termRef.current;
    if (!term || term.options.fontSize === terminalFont) return;
    term.options.fontSize = terminalFont;
    try {
      fitRef.current?.fit();
    } catch {
      // contêiner sem tamanho (aba escondida): ajusta quando aparecer
    }
  }, [terminalFont]);

  useEffect(() => {
    // Próximo quadro: as classes do tema de cor já estão no <html> quando o terminal lê as cores.
    const raf = requestAnimationFrame(() => {
      if (termRef.current) termRef.current.options.theme = terminalTheme(dark);
    });
    return () => cancelAnimationFrame(raf);
  }, [dark, uiTheme]);

  useEffect(() => {
    if (!visible) return;
    const raf = requestAnimationFrame(() => {
      try {
        fitRef.current?.fit();
      } catch {
        // painel recolhido
      }
      termRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  return (
    <div
      ref={host}
      className={cn("min-h-0 flex-1 px-2", !visible && "hidden")}
      role="region"
      aria-label={`Terminal ${n}`}
    />
  );
}

function TabButton({
  id,
  label,
  count,
  alert,
}: {
  id: BottomTab;
  label: string;
  count?: number;
  alert?: boolean;
}) {
  const tab = useWorkspace((s) => s.bottomTab);
  const setTab = useWorkspace((s) => s.setBottomTab);
  const on = tab === id;
  return (
    <button
      role="tab"
      aria-selected={on}
      onClick={() => setTab(id)}
      className={cn(
        "relative flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3 text-[12.5px] outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60",
        on ? "bg-bg-emphasis font-medium text-fg" : "text-fg-subtle hover:bg-bg-muted hover:text-fg",
      )}
    >
      {label}
      {count ? (
        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-bg-emphasis px-1 text-[10.5px] text-fg-muted">
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
      {alert ? (
        <span className="h-1.5 w-1.5 rounded-full bg-[var(--danger)]" aria-label="erros novos" />
      ) : null}
    </button>
  );
}

/** Lista dos terminais (à direita, com dois ou mais), com o indicador de saída e o encerrar. */
function TerminalList() {
  const terminals = useTerminalStore((s) => s.terminals);
  const activeId = useTerminalStore((s) => s.activeId);
  const select = useTerminalStore((s) => s.select);
  return (
    <ul
      className="w-[168px] shrink-0 overflow-y-auto border-l border-border py-1"
      aria-label="Terminais abertos"
    >
      {terminals.map((t) => {
        const on = t.id === activeId;
        const name = `${t.n}: ${t.shell ?? "terminal"}${t.kind === "sandbox" ? " (nuvem)" : ""}`;
        return (
          <li
            key={t.id}
            className={cn(
              "group mx-1 flex h-[26px] items-center gap-2 rounded-md pl-2 pr-0.5 text-[12.5px] transition-colors",
              on ? "bg-bg-emphasis text-fg" : "text-fg-muted hover:bg-bg-muted hover:text-fg",
            )}
          >
            <button
              onClick={() => select(t.id)}
              aria-current={on}
              className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none focus-visible:underline"
              title={`${name} — ${STATUS_LABEL[t.status]}${t.busy ? " · saída chegando" : ""}`}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 shrink-0 rounded-full",
                  t.status === "ready"
                    ? "bg-fg-muted"
                    : t.status === "error"
                      ? "bg-[var(--danger)]"
                      : "bg-fg-subtle",
                  t.busy && "animate-pulse-dot bg-fg",
                )}
              />
              <span className="truncate">{name}</span>
            </button>
            <button
              onClick={() => killTerminal(t.id)}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] opacity-0 hover:bg-bg-muted focus-visible:opacity-100 group-hover:opacity-100"
              aria-label={`Encerrar ${name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function TerminalPanel() {
  const { session } = useWs();
  const role = useRole();
  const open = useWorkspace((s) => s.terminalOpen);
  const tab = useWorkspace((s) => s.bottomTab);
  const problems = useWorkspace((s) => s.problems);
  const setTerminal = useWorkspace((s) => s.setTerminal);
  const stuckDetection = useWorkspace((s) => s.stuckDetection);
  const terminals = useTerminalStore((s) => s.terminals);
  const activeId = useTerminalStore((s) => s.activeId);
  const { status, fs, connection: conn } = useTerminalStore();
  const unseenErrors = useOutput((s) => s.unseenErrors);
  const markSeen = useOutput((s) => s.markSeen);
  const comments = useCommentCount();
  const needsHelp = useHelp((s) => s.needsHelp);
  // Terminal (nuvem ou da máquina): todo papel menos visualizador — quem só visualiza não roda
  // comandos nem leva o código para o computador.
  const sandboxAllowed = can(role, "terminal");
  const allowed = sandboxAllowed;
  const localOn = useLocalCopy((s) => Boolean(s.info?.enabled));
  const activeKind = terminals.find((t) => t.id === activeId)?.kind ?? null;

  // Abrir o painel abre um terminal: no app, o da máquina (se a cópia local está ligada); na web,
  // o do sandbox. O sandbox só é criado quando pedido (custo é o gargalo — princípio 5).
  useEffect(() => {
    if (!open || tab !== "terminal" || useTerminalStore.getState().terminals.length) return;
    // Na web só há o terminal na nuvem (plano Business): sem ele, o painel explica em vez de tentar.
    if (!localCopyAvailable && !hasCloud()) return;
    if (sandboxAllowed && (localCopyAvailable ? localOn : true)) useTerminalStore.getState().add();
  }, [open, sandboxAllowed, localOn, tab]);

  useEffect(() => {
    if (open && tab === "output") markSeen();
  }, [open, tab, unseenErrors, markSeen]);

  const toggleHelp = () => {
    const next = !needsHelp;
    useHelp.setState({ needsHelp: next });
    session.setPresence({ needsHelp: next, activity: next ? "stuck" : "editing" });
    if (!next) {
      toast("Pedido de ajuda removido.");
      // Saiu do mural da rede também (se tinha publicado).
      void del("/discover/help").catch(() => {});
      return;
    }
    // Opt-in: além do time do projeto, a rede de devs pode ver no mural "Precisa de ajuda agora".
    toast("O time vai ver que você precisa de ajuda.", {
      duration: 10_000,
      action: { label: "Publicar no mural da rede", onClick: () => void publishHelp() },
    });
  };

  /** Pede ajuda no mural da página Descobrir; quem ajudar entra como mentor (só sugere). */
  const publishHelp = async () => {
    const ws = activeWorkspace.current;
    if (!ws) return;
    const active = useWorkspace.getState().active;
    const file = active ? ws.session.tree().paths.get(active)?.split("/").pop() : undefined;
    const owner = ws.detail.role === "owner";
    try {
      await post("/discover/help", {
        message: `Travado${file ? ` em ${file}` : ""} no projeto ${ws.detail.project.name}`.slice(0, 280),
        skills: [],
        ...(owner ? { projectId: ws.detail.project.id, allowMentors: true } : { allowMentors: false }),
      });
      toast.success("Publicado no mural", {
        description: owner
          ? "Quem ajudar entra como mentor: só sugere, você aceita ou não."
          : "Quem ajudar manda mensagem (só o dono abre o projeto para mentores).",
      });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const fsBusy = fs && fs.state !== "synced";
  const showTerminal = tab === "terminal";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-10 shrink-0 items-center gap-1 border-b border-panel-border px-2">
        <div
          className="flex h-full shrink-0 items-center gap-0.5"
          role="tablist"
          aria-label="Painel inferior"
        >
          <TabButton id="terminal" label="Terminal" />
          <TabButton
            id="problems"
            label="Problemas"
            count={problems ? problems.errors + problems.warnings : 0}
          />
          <TabButton id="output" label="Saída" alert={unseenErrors > 0 && tab !== "output"} />
          <TabButton id="comments" label="Comentários" count={comments} />
        </div>
        {showTerminal && allowed && terminals.length ? (
          <span className="ml-1 hidden min-w-0 items-center gap-1.5 truncate text-[11.5px] text-fg-muted xl:flex">
            <StatusDot
              state={status === "ready" ? "on" : status === "error" || status === "exited" ? "off" : "busy"}
            />
            {STATUS_LABEL[status]}
            {status === "ready" && fs ? (
              <span className="text-fg-subtle">
                · disco {fsBusy ? `sincronizando (${fs.pending})` : "em dia"}
              </span>
            ) : null}
            <span className="text-fg-subtle">
              {activeKind === "local"
                ? "· na sua máquina, na pasta do projeto"
                : can(role, "write")
                  ? "· na nuvem · o que mudar aqui volta para o projeto"
                  : "· na nuvem · só leitura: o que mudar aqui fica só no seu contêiner"}
            </span>
          </span>
        ) : null}
        <div className="ml-auto flex min-w-0 items-center gap-0.5">
          {allowed && showTerminal ? (
            <>
              {localCopyAvailable ? (
                <Menu>
                  <Tip label={`Novo terminal (Ctrl+Shift+\`)`}>
                    <MenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label="Novo terminal">
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </MenuTrigger>
                  </Tip>
                  <MenuContent align="end" className="w-64">
                    <MenuItem icon={<Laptop className="h-3.5 w-3.5" />} onSelect={() => newTerminal("local")}>
                      Terminal na sua máquina
                    </MenuItem>
                    {sandboxAllowed ? (
                      <MenuItem
                        icon={<Cloud className="h-3.5 w-3.5" />}
                        onSelect={() => newTerminal("sandbox")}
                      >
                        Terminal na nuvem
                      </MenuItem>
                    ) : null}
                  </MenuContent>
                </Menu>
              ) : (
                <Tip label={`Novo terminal (Ctrl+Shift+\`)`}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => newTerminal()}
                    disabled={terminals.length >= MAX_TERMINALS}
                    aria-label="Novo terminal"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </Tip>
              )}
              <Tip label={`Executar (F5 · ${modKey}+Enter)`}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void runProject(session)}
                  aria-label="Executar"
                >
                  <Play className="h-3.5 w-3.5" />
                </Button>
              </Tip>
              <Tip label="Interromper (Ctrl+C)">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => conn?.interrupt()}
                  disabled={status !== "ready"}
                  aria-label="Interromper"
                >
                  <Square className="h-3 w-3" />
                </Button>
              </Tip>
              <Tip label="Reiniciar terminal">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void conn?.restart()}
                  disabled={!conn}
                  aria-label="Reiniciar terminal"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </Tip>
              <Tip label="Encerrar terminal">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => killTerminal()}
                  disabled={!activeId}
                  aria-label="Encerrar terminal"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </Tip>
              <Tip
                label={
                  needsHelp
                    ? "Remover pedido de ajuda"
                    : stuckDetection
                      ? "Pedir ajuda ao time"
                      : "Pedir ajuda ao time (detecção automática desligada)"
                }
              >
                <Button
                  variant={needsHelp ? "primary" : "ghost"}
                  size="icon-sm"
                  onClick={toggleHelp}
                  aria-label="Pedir ajuda"
                  aria-pressed={needsHelp}
                >
                  <LifeBuoy className="h-3.5 w-3.5" />
                </Button>
              </Tip>
            </>
          ) : null}
          <Tip label={`Ocultar painel (${modKey}+J)`}>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setTerminal(false)}
              aria-label="Ocultar painel"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </div>
      </div>
      {tab === "problems" ? <ProblemsList /> : null}
      {tab === "output" ? <OutputList /> : null}
      {tab === "comments" ? <CommentsList /> : null}
      {!allowed ? (
        showTerminal ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center text-[13px] text-fg-muted">
            Visualizadores não têm terminal. Peça ao dono um papel de editor para rodar o projeto no seu
            próprio ambiente.
          </div>
        ) : null
      ) : (
        <>
          {/* Os terminais continuam montados nas outras abas (as sessões não caem). */}
          <div className={cn("flex min-h-0 flex-1", (!showTerminal || !terminals.length) && "hidden")}>
            <div className="flex min-w-0 flex-1 flex-col">
              {terminals.map((t) => (
                <TerminalInstance key={t.id} info={t} visible={showTerminal && t.id === activeId} />
              ))}
            </div>
            {terminals.length > 1 ? <TerminalList /> : null}
          </div>
          {showTerminal && !terminals.length ? (
            localCopyAvailable ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="max-w-md text-[12.5px] leading-relaxed text-fg-muted">
                  O terminal roda na <span className="text-fg">sua máquina</span>, na pasta do projeto — como
                  no VS Code. Para isso o projeto fica salvo numa pasta do seu computador (e continua na nuvem
                  para o time).
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button size="sm" variant="primary" onClick={() => newTerminal("local")}>
                    <Laptop className="h-3.5 w-3.5" />{" "}
                    {localOn ? "Abrir terminal" : "Salvar na máquina e abrir terminal"}
                  </Button>
                  {sandboxAllowed ? (
                    <Button size="sm" variant="secondary" onClick={() => newTerminal("sandbox")}>
                      <Cloud className="h-3.5 w-3.5" /> Usar o terminal na nuvem
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-[12.5px] text-fg-subtle">
                <p className="max-w-md leading-relaxed text-fg-muted">
                  {hasCloud() ? (
                    <>
                      Na web, o terminal roda <span className="text-fg">só na nuvem</span>: um contêiner do
                      Kompil só seu, com os arquivos do projeto. O terminal na sua máquina é do Kompil
                      Desktop.
                    </>
                  ) : (
                    <>
                      Na web, o terminal roda só na nuvem, que é do plano{" "}
                      <span className="text-fg">Business</span>. O terminal na sua máquina é do Kompil
                      Desktop.
                    </>
                  )}
                </p>
                {hasCloud() ? (
                  <Button size="sm" variant="secondary" onClick={() => newTerminal("sandbox")}>
                    <Cloud className="h-3.5 w-3.5" /> Abrir terminal na nuvem
                  </Button>
                ) : null}
              </div>
            )
          ) : null}
        </>
      )}
    </div>
  );
}

/** Problemas do arquivo aberto (os mesmos sublinhados no editor); o clique leva até a linha. */
function ProblemsList() {
  const problems = useWorkspace((s) => s.problems);
  const active = useWorkspace((s) => s.active);
  const tree = useTree();
  const path = active ? tree.paths.get(active) : null;
  // Recalculado a cada render: muda quando o arquivo ou a análise (`problems`) muda.
  const items = (() => {
    const ed = activeEditor.current;
    if (!ed || !problems) return [];
    const out: Array<{ severity: string; message: string; source?: string; line: number; col: number }> = [];
    forEachDiagnostic(ed.view.state, (d, from) => {
      const line = ed.view.state.doc.lineAt(from);
      out.push({
        severity: d.severity,
        message: d.message,
        source: d.source,
        line: line.number,
        col: from - line.from + 1,
      });
    });
    return out.sort((a, b) => a.line - b.line);
  })();
  if (!path || !items.length) {
    return (
      <div className="flex flex-1 items-center justify-center text-[12.5px] text-fg-subtle">
        {path ? "Nenhum problema no arquivo aberto." : "Abra um arquivo para ver os problemas."}
      </div>
    );
  }
  const icon = (sev: string) =>
    sev === "error" ? (
      <CircleX className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--code-error)" }} />
    ) : sev === "warning" ? (
      <TriangleAlert className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--code-warning)" }} />
    ) : (
      <Info className="h-3.5 w-3.5 shrink-0 text-fg-subtle" />
    );
  return (
    <div className="min-h-0 flex-1 overflow-y-auto py-1">
      <p className="px-4 py-1 text-[12px] font-medium text-fg-muted">
        {path} <span className="font-normal text-fg-subtle">· {items.length}</span>
      </p>
      <ul>
        {items.map((it, i) => (
          <li key={i}>
            <button
              onClick={() => activeEditor.current?.revealLine(it.line)}
              className="flex w-full items-start gap-2 px-6 py-1 text-left text-[12.5px] hover:bg-bg-muted focus-visible:bg-bg-muted focus-visible:outline-none"
            >
              <span className="mt-0.5">{icon(it.severity)}</span>
              <span className="min-w-0 flex-1 text-fg">
                {it.message}
                {it.source ? <span className="ml-1.5 text-fg-subtle">{it.source}</span> : null}
              </span>
              <span className="shrink-0 text-[11.5px] text-fg-subtle">
                Ln {it.line}, Col {it.col}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
